/**
 * Collectors routinely Cmd-click 20+ account tabs, then close and restore
 * them as a group. Chrome will start every restored tab's JS at once. If we
 * let each copy of the SPA boot and hit Supabase, the focused tab starves
 * behind ~20 identical membership/customer round-trips.
 *
 * Treat a tab as "active" only when it is visible and not prerendering, and
 * hold non-auth work until then so the tab the user is looking at can load.
 */

export function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') return true
  const doc = document as Document & { prerendering?: boolean }
  if (doc.prerendering) return false
  return document.visibilityState === 'visible'
}

export function waitUntilDocumentVisible(
  signal?: AbortSignal | null
): Promise<void> {
  if (isDocumentVisible()) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onChange)
      document.removeEventListener('prerenderingchange', onChange)
      signal?.removeEventListener('abort', onAbort)
    }

    const onChange = () => {
      if (!isDocumentVisible()) return
      cleanup()
      resolve()
    }

    const onAbort = () => {
      cleanup()
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
    }

    document.addEventListener('visibilitychange', onChange)
    document.addEventListener('prerenderingchange', onChange)

    if (signal) {
      if (signal.aborted) {
        onAbort()
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
    }
  })
}

export function isSupabaseAuthRequest(input: RequestInfo | URL): boolean {
  const url = requestUrl(input)
  return url.includes('/auth/v1/')
}

function requestUrl(input: RequestInfo | URL): string {
  return typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.href
      : input.url
}

function requestMethod(init?: RequestInit): string {
  return (init?.method || 'GET').toUpperCase()
}

/**
 * Only delay/abort PostgREST reads. Writes, RPCs, Storage, and Edge Functions
 * must run to completion — retrying them on tab-focus would duplicate emails,
 * workflow starts, uploads, and payments.
 */
export function shouldDeferSupabaseRequest(
  input: RequestInfo | URL,
  init?: RequestInit
): boolean {
  if (isSupabaseAuthRequest(input)) return false
  if (!requestUrl(input).includes('/rest/v1/')) return false
  if (requestUrl(input).includes('/rest/v1/rpc/')) return false
  const method = requestMethod(init)
  return method === 'GET' || method === 'HEAD'
}

function abortWhenTabHides(): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController()
  const onChange = () => {
    if (isDocumentVisible()) return
    dispose()
    controller.abort(new DOMException('Tab hidden', 'AbortError'))
  }
  const dispose = () => {
    document.removeEventListener('visibilitychange', onChange)
    document.removeEventListener('prerenderingchange', onChange)
  }
  document.addEventListener('visibilitychange', onChange)
  document.addEventListener('prerenderingchange', onChange)
  return { signal: controller.signal, dispose }
}

function mergeAbortSignals(
  a: AbortSignal | null | undefined,
  b: AbortSignal
): { signal: AbortSignal; dispose: () => void } {
  if (!a) return { signal: b, dispose: () => {} }
  if (typeof AbortSignal.any === 'function') {
    return { signal: AbortSignal.any([a, b]), dispose: () => {} }
  }
  const controller = new AbortController()
  const abort = () => controller.abort()
  if (a.aborted || b.aborted) {
    abort()
    return { signal: controller.signal, dispose: () => {} }
  }
  a.addEventListener('abort', abort, { once: true })
  b.addEventListener('abort', abort, { once: true })
  return {
    signal: controller.signal,
    dispose: () => {
      a.removeEventListener('abort', abort)
      b.removeEventListener('abort', abort)
    },
  }
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  )
}

/**
 * Hold PostgREST until this tab is on screen. If the user duplicates the tab
 * and switches away, abort in-flight REST so the new focused tab is not
 * stuck behind 20 still-running copies.
 */
export async function visibilityGatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  if (!shouldDeferSupabaseRequest(input, init)) {
    return fetch(input, init)
  }

  let retryAfterHide = true
  while (retryAfterHide) {
    retryAfterHide = false
    await waitUntilDocumentVisible(init?.signal)
    const hide = abortWhenTabHides()
    const merged = mergeAbortSignals(init?.signal, hide.signal)
    try {
      return await fetch(input, {
        ...init,
        signal: merged.signal,
      })
    } catch (error) {
      if (
        isAbortError(error) &&
        !init?.signal?.aborted &&
        hide.signal.aborted
      ) {
        retryAfterHide = true
        continue
      }
      throw error
    } finally {
      merged.dispose()
      hide.dispose()
    }
  }

  return fetch(input, init)
}
